/**
 * Passport · Section E — Daily Life & Commute
 */
import React from 'react';
import { Footprints, Volume2, Plane, Train, Trophy, Church, GraduationCap, Bus } from 'lucide-react';
import { Badge } from '../ui/badge';

const AMENITY_LABEL_IE = {
  gp: 'GP', school: 'School', pharmacy: 'Pharmacy',
  post: 'Post office', gaa: 'GAA pitch', pub: 'Pub',
};
const AMENITY_LABEL_UK = {
  gp: 'GP', school: 'School', pharmacy: 'Pharmacy',
  post: 'Post office', park: 'Park', pub: 'Pub',
};

const DailyLifeSection = ({ section }) => {
  const d = section.details || {};
  const walk = d.walk || {};
  const sound = d.sound || {};
  const stadium = d.stadium;
  const mass = d.mass;
  const schools = d.schools || [];
  const transport = d.transport;
  const isUK = section.country_label === 'UK';
  const AMENITY_LABEL = isUK ? AMENITY_LABEL_UK : AMENITY_LABEL_IE;

  return (
    <div className="space-y-4 pt-2">
      {/* Walk-Score + Sound row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Walk-Score (Irish flavour on IE / UK flavour on UK) */}
        <div className="rounded-lg border border-stone-200 bg-white p-4" data-testid="passport-walk-card">
          <div className="flex items-center gap-2 mb-2">
            <Footprints className="h-4 w-4 text-purple-700" />
            <span className="font-semibold text-stone-900">Walk-Score {isUK ? '' : '(Irish flavour)'}</span>
          </div>
          <div className="flex items-baseline gap-3">
            <span className="text-3xl font-bold text-purple-700" data-testid="passport-walk-score">
              {walk.score ?? '—'}<span className="text-base text-stone-400 font-normal">/10</span>
            </span>
            <span className="text-xs text-stone-600">{walk.label}</span>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-3">
            {Object.entries(walk.amenities || {}).map(([k, v]) => (
              <Badge
                key={k}
                className={
                  v
                    ? 'bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px] py-0'
                    : 'bg-stone-100 text-stone-400 border-stone-200 text-[10px] py-0 line-through'
                }
              >
                {v ? '✓' : '–'} {AMENITY_LABEL[k] || k}
              </Badge>
            ))}
          </div>
          {walk.estimated && (
            <div className="text-[10px] text-stone-400 mt-2">Estimated — {isUK ? 'Postcode' : 'Eircode'} not in indexed set.</div>
          )}
        </div>

        {/* Sound profile — identical card on IE & UK */}
        <div className="rounded-lg border border-stone-200 bg-white p-4" data-testid="passport-sound-card">
          <div className="flex items-center gap-2 mb-2">
            <Volume2 className="h-4 w-4 text-purple-700" />
            <span className="font-semibold text-stone-900">Sound profile</span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold text-stone-900">
              {sound.daytime_db ?? '—'}<span className="text-sm text-stone-400 font-normal"> dB</span>
            </span>
            <span className="text-xs text-stone-600">{sound.label}</span>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {(sound.details || []).map((d2, idx) => (
              <div key={idx} className="text-[11px] text-stone-700 flex items-center gap-1">
                {d2.toLowerCase().includes('flight') && <Plane className="h-3 w-3 text-amber-600" />}
                {d2.toLowerCase().includes('train') && <Train className="h-3 w-3 text-stone-500" />}
                · {d2}
              </div>
            ))}
          </div>
          <div className="text-[10px] text-stone-400 mt-2">{isUK ? 'Environment Agency strategic noise map.' : 'EPA strategic noise map · DAA flight tracks.'}</div>
        </div>
      </div>

      {/* Stadium + Mass row — identical layout on both markets */}
      {(stadium || mass) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {stadium && (
          <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3" data-testid="passport-stadium-card">
            <div className="flex items-center gap-2 mb-1">
              <Trophy className="h-4 w-4 text-amber-600" />
              <span className="font-semibold text-stone-900">{stadium.stadium}</span>
            </div>
            <div className="text-sm text-stone-800">
              <strong>{stadium.fixtures_per_year}</strong> fixtures / yr ·
              <strong> +{stadium.extra_walkers.toLocaleString()}</strong> pedestrians on match days
            </div>
            <div className="text-[11px] text-stone-600 mt-1">{stadium.note}</div>
          </div>
        )}
        {mass && (
          <div className="rounded-lg border border-stone-200 bg-white p-3" data-testid="passport-mass-card">
            <div className="flex items-center gap-2 mb-1">
              <Church className="h-4 w-4 text-stone-500" />
              <span className="font-semibold text-stone-900">First Sunday Mass</span>
            </div>
            <div className="text-sm text-stone-800">
              {mass.parish} · <strong>{mass.first_sunday_mass}</strong> · {mass.distance_km} km walk
            </div>
            <div className="text-[10px] text-stone-400 mt-1">{isUK ? 'A Church Near You · Church of England parish directory.' : 'Irish Bishops Conference Mass register.'}</div>
          </div>
        )}
        </div>
      )}

      {/* Schools */}
      {schools.length > 0 && (
        <div className="rounded-lg border border-stone-200 bg-white p-4" data-testid="passport-schools-card">
          <div className="flex items-center gap-2 mb-3">
            <GraduationCap className="h-4 w-4 text-purple-700" />
            <span className="font-semibold text-stone-900">Nearest schools</span>
          </div>
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase text-stone-500">
              <tr><th className="text-left font-semibold pb-1">School</th><th className="text-left font-semibold pb-1">Type</th><th className="text-left font-semibold pb-1">Walk</th><th className="text-left font-semibold pb-1">{isUK ? 'Ofsted' : 'Ethos'}</th></tr>
            </thead>
            <tbody>
              {schools.map((s, i) => (
                <tr key={i} className="border-t border-stone-100" data-testid={`passport-school-row-${i}`}>
                  <td className="py-1.5 text-stone-900">{s.name}</td>
                  <td className="py-1.5 text-stone-600">{s.type}</td>
                  <td className="py-1.5 text-stone-600">{s.walk_km} km</td>
                  <td className="py-1.5 text-stone-600">{s.ethos}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="text-[10px] text-stone-400 mt-2">Source: {isUK ? 'Ofsted Data Hub' : 'Department of Education open data'}.</div>
        </div>
      )}

      <div className="text-[10px] text-stone-400">
        {isUK
          ? 'Lifestyle signals from OSM POIs, Ofsted school inspection data, National Rail / TfL / Translink, and postcodes.io.'
          : 'Lifestyle signals from OSM POIs, EPA noise maps, Dept of Education, GAA / IRFU fixtures, and the Irish Catholic Bishops Conference register.'}
      </div>
    </div>
  );
};

export default DailyLifeSection;
